import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import api from "../lib/api";
import {
  Activity, Trophy, Megaphone,
  DollarSign, FileCheck, ChevronRight, RefreshCw,
} from "lucide-react";

const KIND_META = {
  milestone: { icon: Trophy, label: "TIER UP" },
  kyc_approved: { icon: FileCheck, label: "KYC" },
  large_deposit: { icon: DollarSign, label: "DEPOSIT" },
  announcement: { icon: Megaphone, label: "ANNOUNCE" },
};

// Compensation/trial surfaces retired (STARSEED-CAPMARKETS-080). The server no
// longer emits those kinds (routes/dashboard.py suppresses them BEFORE the
// `limit` clip, so they can't crowd out real events). This allowlist is
// belt-and-braces for a stale backend — and it is an ALLOWLIST rather than a
// denylist deliberately: a denylist fails OPEN, rendering any future
// compensation event kind with a generic badge and its full payout copy.
const RENDERABLE_KINDS = new Set(Object.keys(KIND_META));

const TONE_CLS = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  red: "bg-red-50 text-red-700 border-red-200",
  slate: "bg-slate-100 text-slate-600 border-slate-200",
};

/**
 * OpsPulseWidget — live event stream for admins. Polls /dashboard/ops-pulse
 * every 45s, renders each event with a colored icon + one-click action button.
 */
const OpsPulseWidget = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const timer = useRef();

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const r = await api.get("/dashboard/ops-pulse?limit=10");
      setEvents((r.data.events || []).filter((e) => RENDERABLE_KINDS.has(e.kind)));
    } catch {
      // keep existing events, don't alarm the admin
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    timer.current = setInterval(() => load(true), 45_000);
    return () => clearInterval(timer.current);
    // eslint-disable-next-line
  }, []);

  return (
    <Card className="bg-white border-slate-200" data-testid="ops-pulse-widget">
      <CardHeader className="flex-row items-start justify-between">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity size={16} className="text-blue-600" /> Ops Pulse
            <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse-emerald" />
          </CardTitle>
          <CardDescription>
            Live stream · auto-refreshing every 45s · one-click admin actions.
          </CardDescription>
        </div>
        <Button
          onClick={() => load(true)}
          variant="outline"
          size="sm"
          disabled={refreshing}
          data-testid="ops-pulse-refresh"
        >
          <RefreshCw size={14} className={`mr-1 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-6 text-sm text-slate-400">Loading…</div>
        ) : events.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-400">
            No recent activity yet — events will appear as deposits, milestones, and KYC approvals happen.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="ops-pulse-list">
            {events.map((e, i) => {
              const Meta = KIND_META[e.kind] || { icon: Activity, label: "EVENT" };
              const Icon = Meta.icon;
              const toneCls = TONE_CLS[e.tone] || TONE_CLS.blue;
              return (
                <li
                  key={e.id || `${e.kind}-${e.created_at || i}`}
                  className="flex items-start gap-3 py-3 group"
                  data-testid={`ops-pulse-item-${i}`}
                >
                  <div className={`h-8 w-8 rounded-md border flex items-center justify-center shrink-0 ${toneCls}`}>
                    <Icon size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">{Meta.label}</Badge>
                      <p className="text-sm font-medium text-slate-900 truncate">{e.title}</p>
                      <span className="text-xs text-slate-400 shrink-0">{e.rel_time}</span>
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">
                      {e.description}
                    </p>
                    {e.actor && (
                      <p className="text-[11px] text-slate-400 mt-0.5">{e.actor}{e.actor_email ? ` · ${e.actor_email}` : ""}</p>
                    )}
                  </div>
                  {e.action && (
                    <Button
                      onClick={() => e.action.type === "navigate" && navigate(e.action.href)}
                      variant="ghost"
                      size="sm"
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`ops-pulse-action-${i}`}
                    >
                      {e.action.label} <ChevronRight size={12} className="ml-0.5" />
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default OpsPulseWidget;
